import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { SecurityProvider } from "@/components/SecurityProvider";
import { VideoBrightnessProvider } from "@/contexts/VideoBrightnessContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import AdminRoute from "@/components/AdminRoute";
import { useDynamicGA4 } from "@/hooks/useDynamicGA4";
import { lazy, Suspense, useEffect } from "react";
import { prefetchPopularRoutes } from "@/utils/prefetchRoutes";

// Critical pages - load immediately (high-traffic, no loading delay)
import HomeExperimental3 from "./pages/HomeExperimental3";
import HomeExperimental from "./pages/HomeExperimental";
import Pricing from "./pages/Pricing";
import About from "./pages/About";
import HelpCenter from "./pages/HelpCenter";
import Academy from "./pages/Academy";
import NotFound from "./pages/NotFound";
import ScrollToTop from "./components/ScrollToTop";

// Secondary pages - lazy load
const AdminAuth = lazy(() => import("./pages/AdminAuth"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const PasswordReset = lazy(() => import("./pages/PasswordReset"));
const UserSetup = lazy(() => import("./pages/UserSetup"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Blog = lazy(() => import("./pages/Blog"));
const ArticleAdmin = lazy(() => import("./pages/ArticleAdmin"));
const NewArticle = lazy(() => import("./pages/NewArticle"));
const EditArticle = lazy(() => import("./pages/EditArticle"));
const AdminTools = lazy(() => import("./pages/AdminTools").then(m => ({ default: m.AdminTools })));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const CompetitorAnalytics = lazy(() => import("./pages/CompetitorAnalytics"));
const MediaAdmin = lazy(() => import("./pages/MediaAdmin"));
const FreeMarketing = lazy(() => import("./pages/FreeMarketing"));
const PlatformDemos = lazy(() => import("./pages/PlatformDemos"));
const Article = lazy(() => import("./pages/Article"));
const Careers = lazy(() => import("./pages/Careers"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Sitemap = lazy(() => import("./pages/Sitemap"));
const RSSFeed = lazy(() => import("./pages/RSSFeed"));
const SecurityDashboardPage = lazy(() => import("./pages/SecurityDashboard"));
const AgencyOwners = lazy(() => import("./pages/AgencyOwners"));
const DoneForYou = lazy(() => import("./pages/DoneForYou"));
const AIPlatform = lazy(() => import("./pages/AIPlatform"));
const CompareHubspot = lazy(() => import("./pages/CompareHubspot"));
const CompareZoho = lazy(() => import("./pages/CompareZoho"));
const CompareClickfunnels = lazy(() => import("./pages/CompareClickfunnels"));
const InternalLinksAnalytics = lazy(() => import("./pages/InternalLinksAnalytics"));
const KeywordManager = lazy(() => import("./pages/KeywordManager"));
const SeasonalContentPlanner = lazy(() => import("./pages/SeasonalContentPlanner"));
const CompetitorComparison = lazy(() => import("./pages/CompetitorComparison"));
const RSSFeedManager = lazy(() => import("./pages/admin/RSSFeedManager"));
const RSSManagement = lazy(() => import("./pages/admin/RSSManagementEnhanced"));
const KeywordTracking = lazy(() => import("./pages/admin/KeywordTracking"));
const ContentDiscoveries = lazy(() => import("./pages/admin/ContentDiscoveries"));
const ContentPipeline = lazy(() => import("./pages/admin/ContentPipeline"));
const ContentPlanner = lazy(() => import("./pages/admin/ContentPlanner"));

// Theme-aware skeleton fallback - prevents white flash during lazy loading
const PageSkeleton = () => {
  // Read directly from localStorage for instant theme detection (no React state delay)
  const storedTheme = typeof localStorage !== 'undefined' ? localStorage.getItem('editorial-theme') : null;
  const prefersDark = typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
  const isDark = storedTheme === 'dark' || (!storedTheme && prefersDark);
  
  return (
    <div 
      className="min-h-screen"
      style={{ backgroundColor: isDark ? '#111111' : '#FCFCF9' }}
    >
      {/* Header skeleton matching fixed header position */}
      <div 
        className="fixed top-5 left-1/2 -translate-x-1/2 h-10 w-[60%] max-w-[960px] rounded-xl animate-pulse"
        style={{ backgroundColor: isDark ? '#1A1A1A' : '#F0F0F0' }}
      />
    </div>
  );
};

const queryClient = new QueryClient();

const App = () => {
  // Initialize GA4 tracking (non-blocking)
  useDynamicGA4();
  
  // Prefetch secondary routes during idle time
  useEffect(() => {
    prefetchPopularRoutes();
  }, []);


  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <VideoBrightnessProvider>
          <AuthProvider>
            <SecurityProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <BrowserRouter>
                  <Suspense fallback={<PageSkeleton />}>
                    <Routes>
                {/* Public Routes */}
                <Route path="/" element={<HomeExperimental3 />} />
                <Route path="/features" element={<HomeExperimental />} />
                <Route path="/password-reset" element={<PasswordReset />} />
                <Route path="/user-setup" element={<UserSetup />} />
                <Route path="/about" element={<About />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/articles/:slug" element={<Article />} />
                <Route path="/free-marketing" element={<FreeMarketing />} />
                <Route path="/platform-demos" element={<PlatformDemos />} />
                <Route path="/help" element={<HelpCenter />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/academy" element={<Academy />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/sitemap.xml" element={<Sitemap />} />
                <Route path="/rss.xml" element={<RSSFeed />} />
                <Route path="/feed.xml" element={<RSSFeed />} />
                <Route path="/agency-owners" element={<AgencyOwners />} />
                <Route path="/done-for-you" element={<DoneForYou />} />
                <Route path="/ai" element={<AIPlatform />} />
                <Route path="/compare/hubspot" element={<CompareHubspot />} />
                <Route path="/compare/zoho" element={<CompareZoho />} />
                <Route path="/compare/clickfunnels" element={<CompareClickfunnels />} />
                
                {/* Admin Routes */}
                <Route path="/admin" element={<Navigate to="/admin/auth" replace />} />
                <Route path="/admin/auth" element={<AdminAuth />} />
                <Route path="/admin/dashboard" element={
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                } />
                <Route path="/admin/tools" element={
                  <AdminRoute>
                    <AdminTools />
                  </AdminRoute>
                } />
                <Route path="/admin/users" element={
                  <AdminRoute requiredRole="superadmin">
                    <UserManagement />
                  </AdminRoute>
                } />
                <Route path="/admin/articles" element={
                  <AdminRoute requiredRole="admin">
                    <ArticleAdmin />
                  </AdminRoute>
                } />
                <Route path="/admin/articles/new" element={
                  <AdminRoute requiredRole="admin">
                    <NewArticle />
                  </AdminRoute>
                } />
                <Route path="/admin/articles/edit/:id" element={
                  <AdminRoute requiredRole="admin">
                    <EditArticle />
                  </AdminRoute>
                } />
                <Route path="/admin/competitors" element={
                  <AdminRoute requiredRole="admin">
                    <CompetitorAnalytics />
                  </AdminRoute>
                } />
                <Route path="/admin/media" element={
                  <AdminRoute requiredRole="admin">
                    <MediaAdmin />
                  </AdminRoute>
                } />
                <Route path="/admin/security" element={
                  <AdminRoute requiredRole="admin">
                    <SecurityDashboardPage />
                  </AdminRoute>
                } />
                <Route path="/admin/internal-links-analytics" element={
                  <AdminRoute requiredRole="admin">
                    <InternalLinksAnalytics />
                  </AdminRoute>
                } />
                <Route path="/admin/keywords" element={
                  <AdminRoute requiredRole="admin">
                    <KeywordManager />
                  </AdminRoute>
                } />
                <Route path="/admin/seasonal-planner" element={
                  <AdminRoute requiredRole="admin">
                    <SeasonalContentPlanner />
                  </AdminRoute>
                } />
                <Route path="/admin/competitor-comparison" element={
                  <AdminRoute requiredRole="admin">
                    <CompetitorComparison />
                  </AdminRoute>
                } />
                <Route path="/admin/rss-feeds" element={
                  <AdminRoute requiredRole="admin">
                    <RSSFeedManager />
                  </AdminRoute>
                } />
                <Route path="/admin/keyword-tracking" element={
                  <AdminRoute requiredRole="admin">
                    <KeywordTracking />
                  </AdminRoute>
                } />
                <Route path="/admin/content-discoveries" element={
                  <AdminRoute requiredRole="admin">
                    <ContentDiscoveries />
                  </AdminRoute>
                } />
                <Route path="/admin/content-pipeline" element={
                  <AdminRoute requiredRole="admin">
                    <ContentPipeline />
                  </AdminRoute>
                } />
                <Route path="/admin/planner" element={
                  <AdminRoute requiredRole="admin">
                    <ContentPlanner />
                  </AdminRoute>
                } />
                <Route path="/admin/rss-management" element={
                  <AdminRoute requiredRole="admin">
                    <RSSManagement />
                  </AdminRoute>
                } />

                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
                    </Routes>
                </Suspense>
                <ScrollToTop />
              </BrowserRouter>
            </TooltipProvider>
          </SecurityProvider>
        </AuthProvider>
      </VideoBrightnessProvider>
    </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;